import React from "react";
import { ComponentConfig } from "@measured/puck";
import { IEventProps, IEventTicketDetailsProps } from "../../../types/Event";
import { booleanField } from "../helpers";
import { useRootProps } from "../context";

// Event Hero Component
export const EventHero: ComponentConfig<{
  showCoverImage: boolean;
  showName: boolean;
  showDate: boolean;
  showTime: boolean;
  showLocation: boolean;
  showCategory: boolean;
  overlay: boolean;
  overlayOpacity: number;
  backgroundColor: string;
  textColor: string;
}> = {
  fields: {
    showCoverImage: { ...booleanField, label: "Show Cover Image" },
    showName: { ...booleanField, label: "Show Name" },
    showDate: { ...booleanField, label: "Show Date" },
    showTime: { ...booleanField, label: "Show Time" },
    showLocation: { ...booleanField, label: "Show Location" },
    showCategory: { ...booleanField, label: "Show Category" },
    overlay: { ...booleanField, label: "Show Overlay" },
    overlayOpacity: {
      type: "number",
      label: "Overlay Opacity",
      min: 0,
      max: 1,
      step: 0.1,
    },
    backgroundColor: { type: "text", label: "Background Color" },
    textColor: { type: "text", label: "Text Color" },
  },
  defaultProps: {
    showCoverImage: true,
    showName: true,
    showDate: true,
    showTime: true,
    showLocation: true,
    showCategory: false,
    overlay: true,
    overlayOpacity: 0.5,
    backgroundColor: "#17265a",
    textColor: "#ffffff",
  },
  render: (props: any) => {
    const EventHeroComponent: React.FC = () => {
      const {
        showCoverImage,
        showName,
        showDate,
        showTime,
        showLocation,
        showCategory,
        overlay,
        overlayOpacity,
        backgroundColor,
        textColor,
      } = props;
      const { event } = useRootProps();
      const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
        });
      };

      const formatLocation = () => {
        if (!event) return "TBD";
        if (event.type === "online") return "Online Event";
        if (event.type === "hybrid") return "Hybrid Event";
        if (event.location?.address) {
          return `${event.location.address}, ${event.location.city}`;
        }
        return `${event.location?.city || ""}, ${
          event.location?.country || ""
        }`;
      };

      const style: React.CSSProperties = {
        backgroundColor,
        color: textColor,
        backgroundImage:
          showCoverImage && event?.cover_image
            ? `url(${event.cover_image})`
            : undefined,
        backgroundSize: "cover",
        backgroundPosition: "center",
        position: "relative",
        padding: "120px 40px",
        minHeight: "400px",
      };

      return (
        <div style={style}>
          {showCoverImage && event?.cover_image && overlay && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                backgroundColor: `rgba(0, 0, 0, ${overlayOpacity})`,
              }}
            />
          )}
          <div
            style={{
              position: "relative",
              zIndex: 10,
              maxWidth: "1280px",
              margin: "0 auto",
            }}
          >
            {showName && (
              <h1
                style={{
                  fontSize: "48px",
                  fontWeight: "bold",
                  marginBottom: "24px",
                }}
              >
                {event?.name || "Event Name"}
              </h1>
            )}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "24px",
                fontSize: "18px",
              }}
            >
              {showDate && event?.date && <div>{formatDate(event.date)}</div>}
              {showTime && event?.date && (
                <div>
                  {new Date(event.date).toLocaleTimeString("en-US", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </div>
              )}
              {showLocation && <div>{formatLocation()}</div>}
            </div>
          </div>
        </div>
      );
    };
    return <EventHeroComponent />;
  },
};

// Event Description Component
export const EventDescription: ComponentConfig<{
  showEventDescription: boolean;
  showOrganizerDescription: boolean;
  layout: "single-column" | "two-column";
  backgroundColor: string;
  textColor: string;
}> = {
  fields: {
    showEventDescription: { ...booleanField, label: "Show Event Description" },
    showOrganizerDescription: {
      ...booleanField,
      label: "Show Organizer Description",
    },
    layout: {
      type: "select",
      label: "Layout",
      options: [
        { label: "Single Column", value: "single-column" },
        { label: "Two Columns", value: "two-column" },
      ],
    },
    backgroundColor: { type: "text", label: "Background Color" },
    textColor: { type: "text", label: "Text Color" },
  },
  defaultProps: {
    showEventDescription: true,
    showOrganizerDescription: true,
    layout: "single-column",
    backgroundColor: "#ffffff",
    textColor: "#000000",
  },
  render: (props: any) => {
    const EventDescriptionComponent: React.FC = () => {
      const {
        showEventDescription,
        showOrganizerDescription,
        layout,
        backgroundColor,
        textColor,
      } = props;
      const { event } = useRootProps();
      return (
        <div
          style={{ backgroundColor, color: textColor, padding: "60px 40px" }}
        >
          <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  layout === "two-column" ? "repeat(2, 1fr)" : "1fr",
                gap: "32px",
              }}
            >
              {showEventDescription && event?.event_description && (
                <div>
                  <h2
                    style={{
                      fontSize: "24px",
                      fontWeight: "bold",
                      marginBottom: "16px",
                    }}
                  >
                    About This Event
                  </h2>
                  <p style={{ lineHeight: "1.6", color: "#666" }}>
                    {event.event_description}
                  </p>
                </div>
              )}
              {showOrganizerDescription && event?.organizer_description && (
                <div>
                  <h2
                    style={{
                      fontSize: "24px",
                      fontWeight: "bold",
                      marginBottom: "16px",
                    }}
                  >
                    Organizer
                  </h2>
                  <p style={{ lineHeight: "1.6", color: "#666" }}>
                    {event.organizer_description}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    };
    return <EventDescriptionComponent />;
  },
};

// Event Ticket Selection Component
export const EventTicketSelection: ComponentConfig<{
  showTicketTypes: boolean;
  showPrices: boolean;
  showQuantity: boolean;
  showFeatures: boolean;
  layout: "list" | "grid";
  backgroundColor: string;
}> = {
  fields: {
    showTicketTypes: { ...booleanField, label: "Show Ticket Types" },
    showPrices: { ...booleanField, label: "Show Prices" },
    showQuantity: { ...booleanField, label: "Show Quantity" },
    showFeatures: { ...booleanField, label: "Show Features" },
    layout: {
      type: "select",
      label: "Layout",
      options: [
        { label: "List", value: "list" },
        { label: "Grid", value: "grid" },
      ],
    },
    backgroundColor: { type: "text", label: "Background Color" },
  },
  defaultProps: {
    showTicketTypes: true,
    showPrices: true,
    showQuantity: true,
    showFeatures: true,
    layout: "list",
    backgroundColor: "#ffffff",
  },
  render: (props: any) => {
    const EventTicketSelectionComponent: React.FC = () => {
      const {
        showTicketTypes,
        showPrices,
        showQuantity,
        showFeatures,
        layout,
        backgroundColor,
      } = props;
      const { tickets = [] } = useRootProps();
      const displayTickets = tickets as IEventTicketDetailsProps[];

      if (layout === "grid") {
        return (
          <div style={{ backgroundColor, padding: "60px 40px" }}>
            <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
              <h2
                style={{
                  fontSize: "32px",
                  fontWeight: "bold",
                  marginBottom: "32px",
                }}
              >
                Select Tickets
              </h2>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: "24px",
                }}
              >
                {displayTickets.map((ticket) => (
                  <div
                    key={ticket.id}
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      padding: "24px",
                    }}
                  >
                    {showTicketTypes && (
                      <h3
                        style={{
                          fontSize: "20px",
                          fontWeight: 600,
                          marginBottom: "16px",
                        }}
                      >
                        {ticket.ticket_type}
                      </h3>
                    )}
                    {showPrices && (
                      <div
                        style={{
                          fontSize: "24px",
                          fontWeight: "bold",
                          marginBottom: "16px",
                        }}
                      >
                        {typeof ticket.currency === "string"
                          ? ticket.currency
                          : (ticket.currency as any)?.code || "USD"}{" "}
                        {ticket.price}
                      </div>
                    )}
                    {showQuantity && (
                      <div style={{ color: "#666", marginBottom: "16px" }}>
                        {ticket.quantity} available
                      </div>
                    )}
                    {showFeatures && ticket.instructions && (
                      <div style={{ fontSize: "14px", color: "#666" }}>
                        {ticket.instructions}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      }

      return (
        <div style={{ backgroundColor, padding: "60px 40px" }}>
          <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
            <h2
              style={{
                fontSize: "32px",
                fontWeight: "bold",
                marginBottom: "32px",
              }}
            >
              Select Tickets
            </h2>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "16px" }}
            >
              {displayTickets.map((ticket) => (
                <div
                  key={ticket.id}
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    padding: "24px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "start",
                    }}
                  >
                    <div>
                      {showTicketTypes && (
                        <h3
                          style={{
                            fontSize: "20px",
                            fontWeight: 600,
                            marginBottom: "8px",
                          }}
                        >
                          {ticket.ticket_type}
                        </h3>
                      )}
                      {showFeatures && ticket.instructions && (
                        <p
                          style={{
                            fontSize: "14px",
                            color: "#666",
                            marginBottom: "16px",
                          }}
                        >
                          {ticket.instructions}
                        </p>
                      )}
                      {showQuantity && (
                        <div style={{ fontSize: "14px", color: "#666" }}>
                          {ticket.quantity} available
                        </div>
                      )}
                    </div>
                    {showPrices && (
                      <div style={{ fontSize: "24px", fontWeight: "bold" }}>
                        {typeof ticket.currency === "string"
                          ? ticket.currency
                          : (ticket.currency as any)?.code || "USD"}{" "}
                        {ticket.price}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    };
    return <EventTicketSelectionComponent />;
  },
};

// Event Details Component
export const EventDetails: ComponentConfig<{
  showCategory: boolean;
  showAttendees: boolean;
  showAvailability: boolean;
  showDate: boolean;
  showTime: boolean;
  showLocation: boolean;
  backgroundColor: string;
  textColor: string;
}> = {
  fields: {
    showCategory: { ...booleanField, label: "Show Category" },
    showAttendees: { ...booleanField, label: "Show Attendees" },
    showAvailability: { ...booleanField, label: "Show Availability" },
    showDate: { ...booleanField, label: "Show Date" },
    showTime: { ...booleanField, label: "Show Time" },
    showLocation: { ...booleanField, label: "Show Location" },
    backgroundColor: { type: "text", label: "Background Color" },
    textColor: { type: "text", label: "Text Color" },
  },
  defaultProps: {
    showCategory: true,
    showAttendees: true,
    showAvailability: true,
    showDate: true,
    showTime: true,
    showLocation: true,
    backgroundColor: "#f3f4f6",
    textColor: "#000000",
  },
  render: (props: any) => {
    const EventDetailsComponent: React.FC = () => {
      const {
        showCategory,
        showAttendees,
        showAvailability,
        showDate,
        showTime,
        showLocation,
        backgroundColor,
        textColor,
      } = props;
      const { event } = useRootProps();
      const formatLocation = () => {
        if (!event) return "TBD";
        if (event.type === "online") return "Online Event";
        if (event.type === "hybrid") return "Hybrid Event";
        if (event.location?.address) {
          return `${event.location.address}, ${event.location.city}, ${event.location.state}`;
        }
        return `${event.location?.city || ""}, ${
          event.location?.country || ""
        }`;
      };

      return (
        <div
          style={{ backgroundColor, color: textColor, padding: "60px 40px" }}
        >
          <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
            <h2
              style={{
                fontSize: "32px",
                fontWeight: "bold",
                marginBottom: "32px",
              }}
            >
              Event Details
            </h2>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "24px",
              }}
            >
              {showDate && event?.date && (
                <div>
                  <div style={{ fontWeight: 600, marginBottom: "8px" }}>
                    Date
                  </div>
                  <div>
                    {new Date(event.date).toLocaleDateString("en-US", {
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </div>
                </div>
              )}
              {showTime && event?.date && (
                <div>
                  <div style={{ fontWeight: 600, marginBottom: "8px" }}>
                    Time
                  </div>
                  <div>
                    {new Date(event.date).toLocaleTimeString("en-US", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </div>
                </div>
              )}
              {showLocation && (
                <div>
                  <div style={{ fontWeight: 600, marginBottom: "8px" }}>
                    Location
                  </div>
                  <div>{formatLocation()}</div>
                </div>
              )}
              {showAttendees && (
                <div>
                  <div style={{ fontWeight: 600, marginBottom: "8px" }}>
                    Attendees
                  </div>
                  <div>{(event as any)?.event_attendees_count || 0}</div>
                </div>
              )}
              {showAvailability && event?.number_of_seats && (
                <div>
                  <div style={{ fontWeight: 600, marginBottom: "8px" }}>
                    Availability
                  </div>
                  <div>
                    {(event as any)?.event_attendees_count || 0} /{" "}
                    {event.number_of_seats} seats
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    };
    return <EventDetailsComponent />;
  },
};
